"use client";

import { Badge } from "../ui";

import { useMemo, useState } from "react";
import {
  RiArrowLeftLine,
  RiCalendarLine,
  RiFileCopyLine,
} from "react-icons/ri";

import { TodaysAppointments } from "@/components/right-sidebar/patient/todays-appointments";
import Button from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectPills,
  MultiSelectTrigger,
} from "@/components/ui/multi-select";
import { Skeleton } from "@/components/ui/skeleton";

import { clickableText } from "@/lib/tailwind-utils";

import { useCarePlanContext } from "./care-plan";
import {
  AddButton,
  EditForm,
  List,
  StepSequencesProvider,
  Trigger,
  useSequences,
} from "./sequences";
import { ProviderNotesField } from "./step-detail-shared";
import { StepType, isPatientStep } from "./types";

type Mode = "details" | "list" | "editor";

export function AppointmentStepDetail() {
  const {
    state: { selectedStep },
  } = useCarePlanContext();

  if (!selectedStep) return null;

  return (
    <StepSequencesProvider key={selectedStep.id} stepId={selectedStep.id}>
      <Surface />
    </StepSequencesProvider>
  );
}

function Surface() {
  const {
    state: { drafts },
    actions: { add, setEditingKey, discardPending },
  } = useSequences();
  const [history, setHistory] = useState<Mode[]>(["details"]);
  const mode = history[history.length - 1];

  const push = (next: Mode) => setHistory((prev) => [...prev, next]);

  const back = () => {
    setHistory((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.slice(0, -1);
      // Rule #3: never land on an empty list — pop past it.
      if (next[next.length - 1] === "list" && drafts.length === 0) {
        return next.length > 1 ? next.slice(0, -1) : ["details"];
      }
      return next;
    });
  };

  const openList = () => push("list");

  const openNewEditor = () => {
    const key = add();
    setEditingKey(key);
    push("editor");
  };

  const openExistingEditor = (key: string) => {
    setEditingKey(key);
    push("editor");
  };

  const handleBack = () => {
    if (mode === "editor") {
      // Rule #4: pending drafts without content are discarded on back.
      discardPending();
      setEditingKey(null);
    }
    back();
  };

  if (mode === "details") {
    return (
      <DetailsBody
        trigger={<Trigger onShowList={openList} onCreate={openNewEditor} />}
      />
    );
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={handleBack}
        className="inline-flex items-center gap-1 text-caption-strong text-foreground-secondary hover:text-foreground"
      >
        <RiArrowLeftLine size={14} />
        Back
      </button>
      {mode === "list" ? (
        <ListView onPickRow={openExistingEditor} onAddNew={openNewEditor} />
      ) : (
        <EditForm />
      )}
    </div>
  );
}

function ListView({
  onPickRow,
  onAddNew,
}: {
  onPickRow: (key: string) => void;
  onAddNew: () => void;
}) {
  const {
    state: { drafts },
    meta,
  } = useSequences();
  const {
    sequenceCopy: { start: startSequenceCopy },
  } = useCarePlanContext();

  const canCopy = !meta.readOnly && drafts.length > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-caption-emphasis text-foreground">Sequences</h4>
        {canCopy && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => startSequenceCopy(meta.stepId)}
            icon={<RiFileCopyLine size={12} />}
            className="h-6 px-2 text-caption text-foreground-secondary"
          >
            Copy
          </Button>
        )}
      </div>
      <List onPickRow={onPickRow} />
      {!meta.readOnly && <AddButton onCreate={onAddNew} />}
    </div>
  );
}

interface DetailsBodyProps {
  trigger: React.ReactNode;
}

function DetailsBody({ trigger }: DetailsBodyProps) {
  const {
    state: { selectedStep },
    actions: { updateStep },
    config: { readOnly },
    lookups: {
      services,
      medicationOptions,
      measurementOptions,
      isLookupsLoading,
      onScheduleStep,
      schedulableStepIds,
    },
  } = useCarePlanContext();
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  const serviceOptions = useMemo(
    () => services.map((s) => ({ value: s.id, label: s.name })),
    [services],
  );

  if (!selectedStep) return null;

  const patientStep = isPatientStep(selectedStep) ? selectedStep : null;
  const hasServices = selectedStep.serviceIds.length > 0;
  const hasMedications = selectedStep.medicationIds.length > 0;
  const hasMeasurements = selectedStep.measurements.length > 0;

  const canSchedule =
    readOnly &&
    !!onScheduleStep &&
    selectedStep.type === StepType.APPOINTMENT &&
    selectedStep.serviceIds.length > 0 &&
    !!patientStep &&
    schedulableStepIds.has(selectedStep.id);

  const handleServicesChange = (serviceIds: string[]) =>
    updateStep(selectedStep.id, { serviceIds });

  return (
    <div className="space-y-2">
      {/* Services */}
      {(!readOnly || hasServices) && (
        <div className="space-y-0.5">
          <Label>Services</Label>
          {readOnly ? (
            isLookupsLoading ? (
              <div className="flex flex-wrap gap-1.5">
                {selectedStep.serviceIds.map((id) => (
                  <Skeleton key={id} className="h-6 w-24 rounded-full" />
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {selectedStep.serviceIds.map((id) => {
                  const name = serviceOptions.find(
                    (o) => o.value === id,
                  )?.label;
                  return name ? <Badge key={id}>{name}</Badge> : null;
                })}
              </div>
            )
          ) : (
            <MultiSelect
              options={serviceOptions}
              value={selectedStep.serviceIds}
              onChange={handleServicesChange}
            >
              <MultiSelectTrigger placeholder="Select services..." />
              <MultiSelectContent searchPlaceholder="Search services...">
                {serviceOptions.map((opt) => (
                  <MultiSelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MultiSelectItem>
                ))}
              </MultiSelectContent>
              <MultiSelectPills />
            </MultiSelect>
          )}
        </div>
      )}

      {/* Medications */}
      {(!readOnly || hasMedications) && (
        <div className="space-y-0.5">
          <Label>Medications</Label>
          {readOnly ? (
            isLookupsLoading ? (
              <div className="flex flex-wrap gap-1.5">
                {selectedStep.medicationIds.map((id) => (
                  <Skeleton key={id} className="h-7 w-24 rounded-md" />
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {selectedStep.medicationIds.map((id) => {
                  const name = medicationOptions.find(
                    (o) => o.value === id,
                  )?.label;
                  return name ? <Badge key={id}>{name}</Badge> : null;
                })}
              </div>
            )
          ) : (
            <MultiSelect
              options={medicationOptions}
              value={selectedStep.medicationIds}
              onChange={(medicationIds) =>
                updateStep(selectedStep.id, { medicationIds })
              }
            >
              <MultiSelectTrigger placeholder="Select medications..." />
              <MultiSelectContent searchPlaceholder="Search medications...">
                {medicationOptions.map((opt) => (
                  <MultiSelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MultiSelectItem>
                ))}
              </MultiSelectContent>
              <MultiSelectPills />
            </MultiSelect>
          )}
        </div>
      )}

      {/* Measurements */}
      {(!readOnly || hasMeasurements) && (
        <div className="space-y-0.5">
          <Label>Measurements</Label>
          {readOnly ? (
            <div className="flex flex-wrap gap-1.5">
              {selectedStep.measurements.map((m) => {
                const name = measurementOptions.find(
                  (o) => o.value === m.measurementField,
                )?.label;
                return name ? (
                  <Badge key={m.measurementField}>{name}</Badge>
                ) : null;
              })}
            </div>
          ) : (
            <MultiSelect
              options={measurementOptions}
              value={selectedStep.measurements.map((m) => m.measurementField)}
              onChange={(values) =>
                updateStep(selectedStep.id, {
                  measurements: values.map((v) => ({ measurementField: v })),
                })
              }
            >
              <MultiSelectTrigger placeholder="Select measurements..." />
              <MultiSelectContent searchPlaceholder="Search measurements...">
                {measurementOptions.map((opt) => (
                  <MultiSelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MultiSelectItem>
                ))}
              </MultiSelectContent>
              <MultiSelectPills />
            </MultiSelect>
          )}
        </div>
      )}

      <ProviderNotesField />

      {/* Step-level sequences — trigger opens the list/editor */}
      {trigger}

      {/* Schedule action */}
      {canSchedule && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          icon={RiCalendarLine}
          className="w-full"
          onClick={() => onScheduleStep!(selectedStep)}
        >
          Schedule Appointment
        </Button>
      )}

      {/* View linked event */}
      {patientStep?.eventId && (
        <>
          <div
            className={clickableText}
            onClick={() => setEventDialogOpen(true)}
          >
            View Event
          </div>
          <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Appointment Details</DialogTitle>
              </DialogHeader>
              <DialogBody>
                <TodaysAppointments eventId={patientStep.eventId!} />
              </DialogBody>
              <DialogFooter>
                <DialogClose>Close</DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
